import { loadBundleSpec } from '@framebridge/toolbox/BundleCartHelpers';
import FrameSpec from '@framebridge/toolbox/FrameSpec';
import {
  eligibleUpsellProducts,
  frameAllowsUpsells,
  isChildLineOf,
  upsellPriceCents,
  upsellVariant,
} from '@framebridge/toolbox/upsellEligibility';

/**
 * Line-item upsells for cart lines (easel): products tagged cart-upsell in Shopify come from
 * Joinery with their frame dimension limits, and each cart line is matched on its final frame size.
 */
const joineryHost = (SDG?.Data?.joineryHost || '').replace(/\/$/, '');
const TABLETOP_TAG = 'Tabletop Frames';
const productTagsRequests = new Map();
const bundleSpecRequests = new Map();
let productsRequest = null;

function useCartUpsellProducts() {
  const fetchProducts = () => {
    if (!productsRequest) {
      productsRequest = fetch(`${joineryHost}/api/v1/shopify_products_by_tag/cart-upsell`)
        .then((res) => {
          if (!res.ok) throw new Error(`cart upsell products request failed with ${res.status}`);

          return res.json();
        })
        .then((data) => (Array.isArray(data) ? data.map((entry) => entry.product) : []))
        .catch((err) => {
          productsRequest = null;
          console.error('cart upsell products error: ', err);
          return [];
        });
    }

    return productsRequest;
  };

  const productTags = (handle) => {
    if (!productTagsRequests.has(handle)) {
      productTagsRequests.set(handle, fetch(`/products/${handle}.js`)
        .then((res) => {
          if (!res.ok) throw new Error(`product tags request failed with ${res.status}`);

          return res.json();
        })
        .then((product) => product?.tags || [])
        .catch((err) => {
          // unknown tags could hide a tabletop, so a failure offers nothing and is retried later
          productTagsRequests.delete(handle);
          throw err;
        }));
    }

    return productTagsRequests.get(handle);
  };

  const parseFinalFrameSizeProperty = (value) => {
    const match = /([\d.]+)"\s*x\s*([\d.]+)"/.exec(value || '');

    return match ? { width: parseFloat(match[1]), height: parseFloat(match[2]) } : null;
  };

  const lineBundleSpec = (item) => {
    const { _bundle_spec_number: bundleSpecNumber, _bundle_spec_authorization_token: token } = item.properties || {};

    if (!bundleSpecRequests.has(bundleSpecNumber)) {
      bundleSpecRequests.set(bundleSpecNumber, loadBundleSpec(joineryHost, bundleSpecNumber, token)
        .catch((err) => {
          bundleSpecRequests.delete(bundleSpecNumber);
          throw err;
        }));
    }

    return bundleSpecRequests.get(bundleSpecNumber);
  };

  // A bundle's upsell links to its frame like the veneer flow does, so duplicating the bundle keeps it.
  const lineParentDesign = async (item) => {
    const { _frame_spec_number: frameSpecNumber, _bundle_spec_number: bundleSpecNumber } = item?.properties || {};

    if (frameSpecNumber || !bundleSpecNumber) {
      return frameSpecNumber || null;
    }

    try {
      const bundleSpec = await lineBundleSpec(item);

      return bundleSpec?.components?.[0]?.frameSpec?.number || bundleSpecNumber;
    } catch (err) {
      console.error('cart upsell bundle spec error: ', err);

      return bundleSpecNumber;
    }
  };

  const bundleFinalFrameSize = async (item) => {
    const bundleSpec = await lineBundleSpec(item);

    if (bundleSpec?.components?.length !== 1) {
      return null;
    }

    const component = bundleSpec.components[0];
    if (component.frameSpec?.finalFrameSize?.width) {
      return component.frameSpec.finalFrameSize;
    }

    const frameSpec = await FrameSpec.build({ frameSpecData: component.frameSpec, joineryHost, usedFullMats: [] });

    return frameSpec.calculateFinalFrameSize();
  };

  const lineFinalFrameSize = async (item, frameSpec) => {
    if (frameSpec?.finalFrameSize?.width) {
      return frameSpec.finalFrameSize;
    }

    const fromProperty = parseFinalFrameSizeProperty(item.properties?.['Final Frame Size']);
    if (fromProperty) {
      return fromProperty;
    }

    if (frameSpec) {
      return frameSpec.calculateFinalFrameSize();
    }

    if (item.properties?._bundle_spec_number) {
      return bundleFinalFrameSize(item);
    }

    return null;
  };

  const lineFrame = async (item, frameSpec) => ({
    productType: item.product_type,
    isTableTop: frameSpec ? frameSpec.isTableTopFrame() : (await productTags(item.handle)).includes(TABLETOP_TAG),
    isSingleFrame: true,
    isDigital: frameSpec ? frameSpec.isDigital() : !(item.variant_options || []).includes('Physical'),
  });

  const toCartUpsellItem = (product) => {
    const image = (product.media || []).find((media) => media.mediaContentType === 'IMAGE');

    return {
      available: product.status === 'ACTIVE' && Boolean(upsellVariant(product)?.purchasable),
      id: product.shopifyProductId,
      title: product.title,
      price: upsellPriceCents(product),
      featured_image: image?.image?.url || null,
      variants: [{ id: upsellVariant(product)?.shopifyVariantId }],
    };
  };

  const lineUpsellItems = async (item, frameSpec = null) => {
    if (!frameAllowsUpsells({ productType: item.product_type })) {
      return [];
    }

    const products = await fetchProducts();
    if (!products.length) {
      return [];
    }

    try {
      const frame = await lineFrame(item, frameSpec);
      if (!frameAllowsUpsells(frame)) {
        return [];
      }

      const finalFrameSize = await lineFinalFrameSize(item, frameSpec);

      return eligibleUpsellProducts({ products, finalFrameSize, ...frame }).map(toCartUpsellItem);
    } catch (err) {
      console.error('cart upsell size error: ', err);

      return [];
    }
  };

  // Upsell lines already on the frame stay removable even when their product is no longer offered.
  const childUpsellItems = (item, cartLines = [], offeredItems = []) => {
    const offeredVariantIds = offeredItems.map((offered) => `${offered.variants?.[0]?.id}`);

    return cartLines
      .filter((line) => line.properties?._upsell_item === 'true' && isChildLineOf(line, item?.properties))
      .filter((line) => !offeredVariantIds.includes(`${line.variant_id}`))
      .map((line) => ({
        available: true,
        id: line.product_id,
        title: line.product_title || line.title,
        price: line.price,
        featured_image: line.featured_image?.url || line.image || null,
        variants: [{ id: line.variant_id }],
      }));
  };

  return {
    childUpsellItems,
    fetchProducts,
    lineParentDesign,
    lineUpsellItems,
  };
}

export default useCartUpsellProducts;
